import {
  CreateLiveOrderMutation,
  CreateLiveOrderMutationVariables,
  OrderAcquisitionType,
  Shipment,
  ShipmentStopType,
  useCreateLiveOrderMutation,
  useShipmentByIdLazyQuery,
} from "@api/graphql/generated/generated-types";
import {
  BaseModal,
  BaseModalHeader,
  BaseModalBody,
  Button,
  RadioWithLabel,
  Loading,
} from "@src/common/components";
import { useCourieStore } from "@src/common/lib/store";
import { getShipmentName } from "@src/schedules/utils/schedulesNameGenerators";
import CourieButton from "@src/common/components/Button";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { ApolloError, FetchResult } from "@apollo/client";
import { AuthContext } from "@src/auth/components/AuthProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { mapStopAddress } from "@src/orders/create/utils/stopUtils";
import moment from "moment";
import { ExtendedStopCreateInput } from "@src/orders/create/hooks/useNewOrder";
import { type CascadeResult } from "@src/driverCompliancePolicies";
import {
  useDuplicateShipmentSource,
  type DuplicateShipmentSource,
} from "./hooks/useDuplicateShipmentSource";
import { useDuplicateOrderDriverCredentials } from "./hooks/useDuplicateOrderDriverCredentials";

interface DuplicateShipmentModalProps {
  shipmentId: string;
  isModalOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When set (e.g. orders page), loads full shipment after create for edit flow. Omit on pages without ShipmentSelectionContext (e.g. fleet). */
  onCreatedShipmentLoaded?: (shipment: Shipment) => void;
}

const DuplicateShipmentModal: React.FC<DuplicateShipmentModalProps> = ({
  shipmentId,
  isModalOpen,
  onClose,
  onSuccess,
  onCreatedShipmentLoaded,
}) => {
  const { showToast } = useCourieStore();
  const showError = useErrorToast();
  const { courieUser, courierId, employeeId } = useContext(AuthContext);
  const [getShipmentByIdAfterCreate] = useShipmentByIdLazyQuery();
  const [duplicateType, setDuplicateType] = useState<string>("reattempt");
  const [resolvingCredentials, setResolvingCredentials] = useState(false);

  const {
    shipment,
    loading: shipmentByIdLoading,
    error: shipmentByIdError,
    queryData: shipmentByIdData,
  } = useDuplicateShipmentSource({
    shipmentId,
    courierId: courierId ?? undefined,
    skip: !isModalOpen || !shipmentId || !courierId,
  });

  const [createLiveOrderMutation, { loading: createLiveOrderMutationLoading }] =
    useCreateLiveOrderMutation();

  const { resolveDriverCredentials } = useDuplicateOrderDriverCredentials();

  // Opening or closing the modal abandons any submission still waiting on its certification
  // lookup, so dismissing mid-flight can't create the order after the fact.
  const submissionTokenRef = useRef(0);

  useEffect(() => {
    submissionTokenRef.current += 1;
    if (isModalOpen) {
      setDuplicateType("reattempt");
      setResolvingCredentials(false);
    }
  }, [isModalOpen, shipmentId]);

  function getCreateVars(
    source: DuplicateShipmentSource,
    deliveryType: string,
    driverCredentials: CascadeResult,
  ): CreateLiveOrderMutationVariables {
    const now = moment();

    let pickupTimeUsed = false;
    const stops = source.stops.map((stop) => {
      let pickupTime: number | undefined = undefined;
      if (stop.type == ShipmentStopType.PickUp && !pickupTimeUsed) {
        pickupTimeUsed = true;
        pickupTime = Math.round(now.toDate().getTime() / 1000);
      }
      return {
        ...mapStopAddress(stop as ExtendedStopCreateInput),
        stopDate: now.format("YYYY-MM-DD"),
        stopTemplateId: undefined,
        companyName: stop.companyName || undefined,
        timeWindow: {
          open: pickupTime,
          close: undefined,
        },
        type: stop.type,
        suite: stop.suite,
        contactName: stop.contactName,
        phone: stop.phone,
        instruction: stop.instruction,
        // Carried through as-is for createReattempt to use (relative order is all that matters -
        // see withEntrySequenceNumbersFilled below); createReturn explicitly discards it instead,
        // since it has no meaning on a return leg.
        orderEntrySequenceNumber: stop.orderEntrySequenceNumber,
      };
    });

    const variables: CreateLiveOrderMutationVariables = {
      courierId: source.courierId,
      orderTypeId: "2",
      endCustomerId: source.order.endCustomer.id || "",
      acquisitionType: OrderAcquisitionType.User,
      createdBy: employeeId ? employeeId : courieUser!.id,
      originalOrderId: source.order.id,
      requiredDocumentTypes: driverCredentials.required,
      warnedDocumentTypes: driverCredentials.warned,
      shipments: [
        {
          shipmentDate: now.format("YYYY-MM-DD"),
          stops: stops,
          packageWeightInPounds: source.packageWeightInPounds,
          numPackages: source.numPackages,
          packageSize: source.packageSize,
          packageTypeId: source.packageType?.id,
          vehicleTypeId: source.vehicleType?.id,
          packageDescription: [
            source.packageDescription,
            `${deliveryType} of Order #${source.order.displayId}`,
          ]
            .filter(Boolean)
            .join("\n"),
          type: source.type,
        },
      ],
    };

    if (source.order.serviceType) {
      variables.serviceTypeId = source.order.serviceType.id;
    }

    if (source.order.costCenterId) {
      variables.costCenterId = source.order.costCenterId;
    }

    if (source.order.endCustomerReferenceNumber) {
      variables.endCustomerReferenceNumber =
        source.order.endCustomerReferenceNumber;
    }

    if (source.order.caller) {
      variables.caller = source.order.caller;
    }

    const customFieldValues = (source.order.customFieldValues ?? []).map(
      (customField) => ({
        fieldId: customField.fieldId,
        valueText: customField.valueText || "",
        valueNumber: customField.valueNumber || 0,
        valueInteger: customField.valueInteger || 0,
      }),
    );
    if (customFieldValues.length > 0) {
      variables.customFieldValues = customFieldValues;
    }

    return variables;
  }

  function createOneOffOrder(
    data: CreateLiveOrderMutationVariables,
    callback?: (error: any, result: any) => void,
  ) {
    if (!Array.isArray(data.shipments)) return;

    createLiveOrderMutation({ variables: data })
      .then((res) => {
        callback && callback(null, res);
        const trackingNumber =
          res.data?.createLiveOrder.shipment?.trackingNumber;
        const customerName = res.data?.createLiveOrder.endCustomer?.companyName;
        showToast(
          {
            message: "Order created successfully",
            type: "info",
            position: "bottom-right",
            secondaryText: `
              <div">
                <div>Customer: <span class="font-bold">${customerName}</span></div>
                <div>Tracking Number: <span class="font-bold">${trackingNumber}</span></div>
              </div>
            `,
            action: {
              label: "View Order",
              href: `/deliveries/orders?filters=${encodeURIComponent(
                JSON.stringify({ trackingNumber }),
              )}`,
            },
          },
          10000,
        );
      })
      .catch((e) => {
        callback && callback(e, null);
        showErrorToast(e, showToast);
      });
  }

  const orderCreationCallback = (
    error: ApolloError,
    response: FetchResult<CreateLiveOrderMutation>,
  ) => {
    if (error) {
      showErrorToast(error, showToast);
      return;
    }
    if (!response.data) {
      showToast({
        message: "Shipment not found",
        type: "error",
      });
      return;
    }
    const finishAfterCreate = (created: Shipment | undefined) => {
      onClose();
      if (created && onCreatedShipmentLoaded) {
        onCreatedShipmentLoaded(created);
      }
      onSuccess();
    };

    if (onCreatedShipmentLoaded) {
      getShipmentByIdAfterCreate({
        variables: {
          shipmentId: response.data?.createLiveOrder.shipment?.id || "",
          courierId: courierId || "",
        },
      })
        .then((res) => {
          finishAfterCreate(res.data?.shipmentById as Shipment | undefined);
        })
        .catch((err) => {
          showErrorToast(err, showToast);
          onClose();
        });
    } else {
      finishAfterCreate(undefined);
    }
  };

  // Ensures every stop of a Reattempt has a non-null orderEntrySequenceNumber - a single null
  // permanently disables "keep order entry sequence" dispatch ordering for the whole shipment
  // (StopServiceImpl.makeStopsSoftRoute requires every stop to have one; see #3392).
  //
  // Why a null happens at all: a duplicated order's stops never get a value from duplication
  // itself (see getCreateVars/useDuplicateShipmentSource above), and a later edit that adds a
  // stop only numbers *that new stop* - the backend's OrderEntrySequenceStamper never goes back
  // and fills in the original copied ones. So a Reattempt-of-a-Reattempt, or any order duplicated
  // before this fix existed, can land here with a *mix*: some stops already correctly numbered
  // (from edits), others still null (from the original copy) - exactly #3392's real shape (2 null
  // from the copy, 3 numbered from later edits).
  //
  // When every stop already has a value, they're returned unchanged - cube only ever compares
  // this field by relative order (StopServiceImpl.makeStopsSoftRoute, ShipmentRouteMaker,
  // OrderEntrySequenceStamper all just sort/max it; nothing requires it gap-free or 1-based).
  //
  // Otherwise, only the null stops get a fresh number - mirroring OrderEntrySequenceStamper's own
  // algorithm exactly: append starting at (current max + 1), in array order, and leave every
  // already-numbered stop's value untouched. Filling every stop from array position instead
  // (index + 1) would silently overwrite and renumber the already-correct ones too - not just the
  // missing ones - which is its own version of the bug this function exists to fix.
  function withEntrySequenceNumbersFilled(
    data: CreateLiveOrderMutationVariables,
  ): CreateLiveOrderMutationVariables {
    if (!Array.isArray(data.shipments)) return data;
    return {
      ...data,
      shipments: data.shipments.map((shipmentInput) => {
        const everyStopHasSequenceNumber = shipmentInput.stops.every(
          (stop) => stop.orderEntrySequenceNumber != null,
        );
        if (everyStopHasSequenceNumber) return shipmentInput;

        const maxExistingSequenceNumber = Math.max(
          0,
          ...shipmentInput.stops
            .map((stop) => stop.orderEntrySequenceNumber)
            .filter(
              (sequenceNumber): sequenceNumber is number =>
                sequenceNumber != null,
            ),
        );
        let nextSequenceNumber = maxExistingSequenceNumber + 1;
        return {
          ...shipmentInput,
          stops: shipmentInput.stops.map((stop) =>
            stop.orderEntrySequenceNumber != null
              ? stop
              : { ...stop, orderEntrySequenceNumber: nextSequenceNumber++ },
          ),
        };
      }),
    };
  }

  const createReattempt = (driverCredentials: CascadeResult) => {
    if (!shipment) return;
    const data = getCreateVars(shipment, "Reattempt", driverCredentials);
    createOneOffOrder(
      withEntrySequenceNumbersFilled(data),
      orderCreationCallback,
    );
  };

  const createReturn = (driverCredentials: CascadeResult) => {
    if (!shipment) return;
    const baseData = getCreateVars(shipment, "Return", driverCredentials);
    if (baseData.shipments[0].stops.length !== 2) {
      showToast({
        message: "Return must have 2 stops",
        type: "error",
      });
      return;
    }
    const data = {
      ...baseData,
      shipments: [
        {
          ...baseData.shipments[0],
          stops: [
            {
              ...baseData.shipments[0].stops[1],
              type: ShipmentStopType.PickUp,
              // No meaningful entry order on a return leg - see getCreateVars/ReturnStopMaker.
              orderEntrySequenceNumber: undefined,
              timeWindow: {
                open: baseData.shipments[0].stops[0].timeWindow.open,
                close: baseData.shipments[0].stops[0].timeWindow.close,
              },
            },
            {
              ...baseData.shipments[0].stops[0],
              type: ShipmentStopType.DropOff,
              orderEntrySequenceNumber: undefined,
              timeWindow: {
                open: baseData.shipments[0].stops[1].timeWindow.open,
                close: baseData.shipments[0].stops[1].timeWindow.close,
              },
            },
          ],
        },
      ],
    };
    createOneOffOrder(data, orderCreationCallback);
  };

  const duplicateShipmentSubmit = () => {
    if (!shipment) return;
    if (duplicateType !== "reattempt" && duplicateType !== "return") {
      showToast({
        message: "Invalid duplicate type",
        type: "error",
      });
      return;
    }
    setResolvingCredentials(true);
    const submissionToken = ++submissionTokenRef.current;
    resolveDriverCredentials(shipment)
      .then((driverCredentials) => {
        if (submissionToken !== submissionTokenRef.current) return;
        setResolvingCredentials(false);
        if (duplicateType === "reattempt") {
          createReattempt(driverCredentials);
          return;
        }
        createReturn(driverCredentials);
      })
      .catch((err) => {
        if (submissionToken !== submissionTokenRef.current) return;
        setResolvingCredentials(false);
        showError(err as ApolloError);
      });
  };

  const headerTitle = shipment
    ? `Duplicate ${getShipmentName(shipment)}`
    : "Duplicate order";

  const showLoadError =
    !shipmentByIdLoading &&
    (shipmentByIdError ||
      (shipmentByIdData !== undefined && !shipment));

  return (
    <BaseModal isOpen={isModalOpen} onClose={onClose}>
      <BaseModalHeader>{headerTitle}</BaseModalHeader>
      <BaseModalBody>
        {shipmentByIdLoading && (
          <div className="flex justify-center py-12">
            <Loading loading />
          </div>
        )}
        {showLoadError && (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              {shipmentByIdError
                ? "Could not load shipment. Try again or close this dialog."
                : "Shipment not found."}
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose} color="gray">
                Close
              </Button>
            </div>
          </div>
        )}
        {!shipmentByIdLoading && shipment && (
          <>
            <div>Choose a duplication method.</div>
            <div className="flex gap-3 pt-4">
              <RadioWithLabel
                id="reattempt"
                name="reattempt"
                value="reattempt"
                htmlFor="reattempt"
                checked={duplicateType === "reattempt"}
                onChange={() => setDuplicateType("reattempt")}
              >
                <span>Reattempt</span>
              </RadioWithLabel>
              <div className="text-sm text-gray-500 flex items-center">
                Creates an exact duplicate of the original order.
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <RadioWithLabel
                id="return"
                name="return"
                value="return"
                htmlFor="return"
                checked={duplicateType === "return"}
                onChange={() => setDuplicateType("return")}
              >
                <span>Return</span>
              </RadioWithLabel>
              <div className="text-sm text-gray-500 flex items-center">
                Creates an order where the original order&apos;s pickup and
                dropoff are swapped.
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button onClick={onClose} color="gray">
                Cancel
              </Button>
              <CourieButton
                type="button"
                data-cy="duplicate-shipment-button"
                isProcessing={
                  resolvingCredentials || createLiveOrderMutationLoading
                }
                disabled={
                  resolvingCredentials || createLiveOrderMutationLoading
                }
                onClick={duplicateShipmentSubmit}
              >
                Duplicate
              </CourieButton>
            </div>
          </>
        )}
      </BaseModalBody>
    </BaseModal>
  );
};

export default DuplicateShipmentModal;
